<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router';
import { useGetAuthUrl } from '@/composables/microservice/lexington/useGetAuthUrl';
import LoginLink from 'theme/components/atoms/LoginLink.vue';
import NuxtLinkFlat from 'theme/components/atoms/NuxtLinkFlat.vue';
import BalloonPopup from 'theme/components/molecules/BalloonPopup.vue';
import ButtonLineLogin from 'theme/components/molecules/ButtonLineLogin.vue';
import LineLoginPopup from 'theme/components/molecules/LineLoginPopup.vue';
import RegisterUserHeading from 'theme/components/molecules/RegisterUserHeading.vue';
import PromptUserToLoginWithDefaultBrowser from 'theme/components/templates/LoginTemplate/PromptUserToLoginWithDefaultBrowser.vue';
import { getStringQuery } from 'theme/utils/getQuery';
import PolicyLinks from '~/components/organisms/PolicyLinks/PolicyLinks.vue';

const lineAuthUrl = ref('');

const route = useRoute();

const registerEmail = computed((): RouteLocationRaw => {
  return {
    path: '/register/email',
    query: route.query,
  };
});

const {
  PromptUserToLoginWithDefaultBrowserModal,
  openPromptUserToLoginWithDefaultBrowserModal,
} = useModalNamed('promptUserToLoginWithDefaultBrowser');

onBeforeMount(async () => {
  const redirect = getStringQuery(route.query.redirect);

  const session = useSession();
  const { channelId: landingChannelId } = useChannelId();
  const searchParams = {
    redirect: redirect ?? '/my',
    channelId: landingChannelId.value,
    shortSessionId: session.shortSessionId.value,
    longSessionId: session.longSessionId.value,
  };

  const { fetch } = useGetAuthUrl();
  try {
    lineAuthUrl.value = await fetch(searchParams);
  } catch (_) {
    // TODO: エラーハンドリングする
  }
});
</script>

<template>
  <main class="main">
    <slot name="header">
      <RegisterUserHeading class="main__heading" />
    </slot>
    <slot name="contents" />
    <div class="main__line line">
      <LineLoginPopup class="line__popup" />
      <ButtonLineLogin
        :auth-url="lineAuthUrl"
        class="line__link"
        :title="'LINEではじめる'"
        @try-to-login-with-instagram-browser="
          openPromptUserToLoginWithDefaultBrowserModal
        "
      />
      <p class="lineNotice">
        LINEではじめる場合はTwitterなどのアプリ内ではなく、いつもご利用いただいているブラウザをご利用ください。
      </p>
    </div>
    <NuxtLinkFlat
      class="main__email email"
      variant="primary"
      :to="registerEmail"
    >
      メールアドレスではじめる
    </NuxtLinkFlat>
    <PolicyLinks class="main__policies" />
    <LoginLink class="main__login" flat>会員登録済みの方はこちら</LoginLink>
    <div class="main__sales-point sales-point">
      <p class="sales-point__head sales-point-head">
        オカネコ<strong class="sales-point-head__emphasis">5</strong>つの安心
      </p>
      <BalloonPopup class="sales-point__balloon balloon">
        <ol class="balloon__list list">
          <li class="list__item">
            あなたと似た人達の貯金や投資額と比較できるにゃ！
          </li>
          <li class="list__item">
            相談した人の<strong class="item__emphasis">95%</strong
            >が満足と回答してるにゃ！
          </li>
          <li class="list__item">&nbsp;強引なセールスなどはないにゃ！</li>
          <li class="list__item">&nbsp;匿名、無料で相談ができるにゃ！</li>
          <li class="list__item">&nbsp;お家からいつでも相談ができるにゃ！</li>
        </ol>
      </BalloonPopup>
      <img class="sales-point__image" src="./okaneko.svg" alt="" />
    </div>
    <PromptUserToLoginWithDefaultBrowserModal>
      <PromptUserToLoginWithDefaultBrowser />
    </PromptUserToLoginWithDefaultBrowserModal>
  </main>
</template>

<style lang="scss" scoped>
.main {
  display: flex;
  flex-direction: column;

  &__line {
    &:nth-child(n + 2) {
      margin: 32px 32px 0;
    }
  }

  &__email {
    &:nth-child(n + 2) {
      margin: 20px 32px 0;
    }
  }

  &__policies {
    display: flex;
    justify-content: center;

    font-size: 12px;
    margin-top: 20px;
  }

  &__login {
    &:nth-child(n + 2) {
      margin: 32px 32px 0;
    }
  }

  &__sales-point {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.line {
  &__popup {
    margin: 0 20px;

    @include small-mobile-only {
      margin: 0;
    }
  }

  &__link {
    width: 100%;
    display: flex;

    @include small-mobile-only {
      font-size: 14px;
    }

    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.lineNotice {
  font-size: 12px;
  line-height: 1.3;
  font-weight: bold;
  color: $color-third-party-line;

  &:nth-child(n + 2) {
    margin-top: 4px;
  }
}

.email {
  @include small-mobile-only {
    font-size: 14px;
  }
}

.sales-point {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__head {
    font-weight: bold;
    font-size: 18px;
  }

  &__balloon {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__image {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.sales-point-head {
  &__emphasis {
    margin: 0 2px;
    vertical-align: -1px;
    font-size: 28px;
    color: $color-text-and-object-accent-primary;
  }
}

.list {
  counter-reset: item;

  &__item {
    list-style: none;

    font-weight: bold;
    font-size: 12px;
    letter-spacing: 0;
    line-height: 24px;

    &:before {
      counter-increment: item;
      content: counter(item) '.';

      color: $color-text-and-object-accent-primary;
    }
  }
}

.item {
  &__emphasis {
    color: $color-text-and-object-accent-primary;
  }
}
</style>
